/**
 * `hoverIntel` — what hovering a unit paints on the board (`projects/hover-intel.md` Phase 1): its
 * move reach as a blue zone and one red ring per distinct weapon reach, cut to what the viewer
 * knows about it.
 *
 * **The wire carries full enemy data** (`masking-display-only`), so every zone is gated on the
 * render-time projection, the same test `moveForecastFor` applies: the move zone needs the
 * stat block (Speed), so only an unmasked unit gets one; the rings read the projected
 * `base.weapons`, which a `"class"`-tier hostile does not carry. Counting blue cells must never
 * reveal a Speed the stat card shows as `??`.
 *
 * Everything here is display-only. The move reach reads a planning state for a unit that isn't
 * acting, which is never committed or dispatched (`planningTurnState`'s invariant).
 *
 * **Rings respect cover** (`projects/range-preview-cover.md`, reversing hover-intel's "no LoS
 * clipping"): a ring keeps a cell only if some weapon at that reach could target it, and the cells
 * where cover would change a weapon's to-hit roll come back as one hatch map. Line of sight from a
 * known position over public terrain leaks nothing the projection hides.
 */
import {
  attackReachCellList,
  sightlinesFrom,
  worldFor,
  isCharacter,
  isItemDestroyed,
  planningTurnState,
  primaryReach,
  projectCombatantFor,
  reachableCells,
  weaponInstanceKey,
  type ActorRole,
  type Cell,
  type Combatant,
  type EncounterState,
  type ReachCell,
  type CellSight,
  type WeaponProfile,
} from "@lancer-vtt/domain";
import { alliedCoverCells, type LoadedOverlay } from "@lancer-vtt/rules";
import { rangeThisTurn } from "./attackRange";
import { coverAppliesTo, losExemptionFor, type CoverWeapon } from "./combatInspector";
import { isMaskedCombatant } from "./disclosure";

/**
 * One red ring: the cells within `reach` of the unit's footprint that some weapon at that reach
 * can target, and the weapons that reach it.
 */
export type ThreatRing = { reach: number; keys: Set<string>; weapons: string[] };

/** The cover grade under the rings, for the hatch: only cells where cover changes a ring weapon's to-hit roll. */
export type RingCover = Map<string, "soft" | "hard">;

/**
 * The zones hover paints for one unit. `move` is `null` when the viewer may not see its Speed.
 * `passMs` is how long the sightline pass took (0 for plain rings), which a drag reads to decide
 * whether it can afford cover-aware rings per cell.
 */
export type HoverIntel = { move: Set<string> | null; rings: ThreatRing[]; cover: RingCover; passMs: number };

/** What the board draws for the rings: each ring's cells and the hatch beneath them. */
export type ThreatRingsView = { rings: readonly Set<string>[]; cover: ReadonlyMap<string, "soft" | "hard"> };

/**
 * The zones for hovering `hoveredId`, as `viewer` may see them, or `null` for a non-character, an
 * unplaced unit or an unknown id (those keep today's hover label and nothing else).
 *
 * - **Move** (F1): only when the projection leaves the unit unmasked (own, allied, the GM, a
 *   full-view grant, or a `"full"`-disclosed hostile). The acting unit shows its remaining move off
 *   the live state; any other unit a fresh turn's reach where it stands (`planningTurnState`).
 *   A status bar (Immobilized) still empties it, because `reachableCells` is the server's wall.
 * - **Rings** (F2): measured from where the unit stands now. Each known weapon and alternate
 *   profile contributes its {@link primaryReach}, the armed wash's radius, so a ring equals the
 *   wash that weapon paints when armed. Deduped by reach, largest first. A destroyed weapon and a
 *   latched ranged-or-melee weapon's other mode drop out only when the viewer sees `current`.
 * - **`at`** (`projects/intel-follows-selection.md`): a drag's would-be drop cell. The rings
 *   measure from there instead, and there is no move zone: the drag keeps the movement wash it
 *   started with, which marks where a drop is legal.
 * - **Cover** (`range-preview-cover` F2): a cell stays in a ring when any of its weapons can target
 *   it, so Arcing and Seeking rings keep every cell. The hatch is the cover on cells kept by a weapon
 *   that {@link coverAppliesTo}: ranged and Arcing, never melee, Blast or Seeking. One sightline pass
 *   over the outermost ring serves every ring, memoized per (state, unit, origin, reach).
 */
export function hoverIntelFor(args: {
  state: EncounterState;
  hoveredId: string;
  viewer: { role: ActorRole; viewerSlot: string | null };
  at?: Cell;
  /** The merged overlay, so a Guardian's allied hard cover hatches as the shot would fold it. */
  overlay?: LoadedOverlay;
  /** Plain geometric rings with no sightline pass: a slow drag's per-cell fallback. */
  plain?: boolean;
}): HoverIntel | null {
  const { state, hoveredId, viewer, at, overlay, plain } = args;
  const c = state.combatants[hoveredId];
  if (!c || !isCharacter(c) || c.pos == null) return null;
  const seen = projectCombatantFor({ combatant: c, viewerSlot: viewer.viewerSlot, role: viewer.role, state });
  const masked = isMaskedCombatant(seen);
  const weapons: WeaponProfile[] = "weapons" in seen.base ? (seen.base.weapons ?? []) : [];
  const unit = at ? { ...c, pos: at } : c;
  const reaches = weaponReaches({ state, c: unit, weapons, seeCurrent: !masked });
  const outer = reaches.length > 0 ? reaches[0]!.cells : [];
  const pass = plain || outer.length === 0 ? null : sightlinePass({ state, unit, cells: outer, overlay });
  return {
    move: masked || at ? null : moveReach(state, c),
    ...threatRings(reaches, pass?.sights ?? null),
    passMs: pass?.ms ?? 0,
  };
}

function moveReach(state: EncounterState, c: Combatant): Set<string> | null {
  const turn = state.activation.turn;
  const acting = turn.phase === "acting" && turn.actor === c.id;
  const planned = acting ? state : planningTurnState(state, c.id);
  const unit = planned.combatants[c.id];
  return unit ? reachableCells(planned, unit) : null;
}

/** One distinct reach: its geometric cells (on and off the board) and each weapon profile at it. */
type Reach = { reach: number; cells: ReachCell[]; weapons: Array<{ name: string; profile: CoverWeapon }> };

/** Each distinct {@link primaryReach} among the known weapons and their profiles, largest first. */
function weaponReaches(args: { state: EncounterState; c: Combatant; weapons: WeaponProfile[]; seeCurrent: boolean }): Reach[] {
  const { state, c, weapons, seeCurrent } = args;
  const byReach = new Map<number, Reach>();
  for (const weapon of weapons) {
    if (seeCurrent && isItemDestroyed(c.current.resources, weaponInstanceKey(weapon))) continue;
    for (const profile of [weapon, ...(weapon.profiles ?? [])]) {
      const full = profile.range ?? [];
      const range = seeCurrent ? rangeThisTurn({ attacker: c, weapon, range: full, turnOrdinal: state.activation.turnOrdinal }) : full;
      const reach = primaryReach(range);
      if (reach <= 0) continue;
      const entry = { name: weapon.name, profile: { range, tags: profile.tags ?? weapon.tags } };
      const known = byReach.get(reach);
      if (known) {
        known.weapons.push(entry);
        continue;
      }
      const cells = attackReachCellList(state, c, range);
      if (cells) byReach.set(reach, { reach, cells, weapons: [entry] });
    }
  }
  return [...byReach.values()].sort((a, b) => b.reach - a.reach);
}

/**
 * The rings and their hatch from one sightline pass, or the plain geometric rings with no hatch
 * when `sights` is `null` (the board clips their off-board cells itself).
 */
function threatRings(reaches: Reach[], sights: Map<string, CellSight> | null): { rings: ThreatRing[]; cover: RingCover } {
  const cover: RingCover = new Map();
  const rings: ThreatRing[] = [];
  for (const { reach, cells, weapons } of reaches) {
    const names = [...new Set(weapons.map((w) => w.name))];
    if (sights === null) {
      rings.push({ reach, keys: new Set(cells.map((r) => r.key)), weapons: names });
      continue;
    }
    const ignoresLos = weapons.some((w) => losExemptionFor(w.profile) !== null);
    const hatches = weapons.filter((w) => coverAppliesTo(w.profile));
    const keys = new Set<string>();
    for (const { key } of cells) {
      const sight = sights.get(key);
      if (!sight || !(sight.visible || ignoresLos)) continue;
      keys.add(key);
      if (sight.cover === "none") continue;
      if (hatches.some((w) => sight.visible || losExemptionFor(w.profile) !== null)) {
        if (cover.get(key) !== "hard") cover.set(key, sight.cover);
      }
    }
    rings.push({ reach, keys, weapons: names });
  }
  return { rings, cover };
}

/**
 * Memoized sightline passes, per state: the same (unit, origin, outer reach, overlay) on the same
 * state reuses the pass, so a hovered unit that is also selected, or a re-render, costs nothing.
 */
const passCache = new WeakMap<EncounterState, Map<string, { overlay: LoadedOverlay | undefined; sights: Map<string, CellSight>; ms: number }>>();

function sightlinePass(args: { state: EncounterState; unit: Combatant; cells: ReachCell[]; overlay: LoadedOverlay | undefined }): { sights: Map<string, CellSight>; ms: number } {
  const { state, unit, cells, overlay } = args;
  const world = worldFor(state);
  const key = `${unit.id}@${world.key(unit.pos!)}#${cells.length}`;
  let byKey = passCache.get(state);
  if (!byKey) {
    byKey = new Map();
    passCache.set(state, byKey);
  }
  const cached = byKey.get(key);
  if (cached && cached.overlay === overlay) return cached;
  const t0 = performance.now();
  const sights = sightlinesFrom({
    state,
    from: world.footprint(unit.pos!, unit.base.size).cells(),
    cells,
    ...(overlay !== undefined ? { extraHardCoverFor: (occupant: string) => alliedCoverCells({ state, targetId: occupant, attackerId: unit.id, overlay }) } : {}),
  });
  const pass = { overlay, sights: new Map(sights.map((s) => [s.key, s])), ms: performance.now() - t0 };
  byKey.set(key, pass);
  return pass;
}
